import {
  BuildingCertificationSettings,
  BuildingIN,
  BuildingOUT,
  BuildingUpdateIN,
  IngestionOptionsSchema,
  PlaceDetailsOUT,
  SuggestOUT,
  buildingsApi,
  getLocalAuthHeader,
  locationsApi,
  BuildingCalculationEngine,
  licensesApi,
} from "api-client";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { makeAutoObservable } from "mobx";
import { buildingStore } from "store/Building/BuildingStore";
import { organisationStore } from "features/Organisation/OrganisationStore";
import {
  BUILDING_TYPES,
  placeDetailsToString,
  certificationIdsToKeys,
} from "utils";
import moment from "moment";
import { DGNBStore } from "store/DGNBStore";
import { conformityStore } from "store/ConformityStore";
import { AxiosResponse } from "axios";

export type BuildingSettingsProps = Partial<BuildingOUT> &
  Partial<BuildingCertificationSettings> & {
    building_type_id?: string;
    string_address?: string;
    selectedImages?: File[];
    certification_id?: string;
    conformity_id?: string;
    use_imported_mass?: boolean;
    calculation_mode?: BuildingCalculationEngine;
  };

export type FormValue =
  | string
  | number
  | undefined
  | File[]
  | Date
  | object
  | boolean
  | null;

export interface CompliantObjectsProps {
  compliant?: number;
  total?: number;
}

class BuildingSettingsStore {
  settings: BuildingSettingsProps = {};
  showMandatoryAlert = false;
  imagesToRemove: string[] = [];
  informationErrors = false;
  certificationErrors = false;
  openCertificationsTab: boolean = false;
  openEditBuilding = false;
  buildingAlreadyUpdated: boolean = false;
  compliantObjects: CompliantObjectsProps = {};

  setSettings(settings: BuildingSettingsProps) {
    this.settings = settings;
  }

  setshowMandatoryAlert(showMandatoryAlert: boolean) {
    this.showMandatoryAlert = showMandatoryAlert;
  }

  setImagesToRemove(imagesToRemove: string[]) {
    this.imagesToRemove = imagesToRemove;
  }

  setOpenCertificationsTab(openCertificationsTab: boolean) {
    this.openCertificationsTab = openCertificationsTab;
  }

  setOpenEditBuilding(openEditBuilding: boolean) {
    this.openEditBuilding = openEditBuilding;
  }

  setBuildingAlreadyUpdated(buildingAlreadyUpdated: boolean) {
    this.buildingAlreadyUpdated = buildingAlreadyUpdated;
  }

  setCompliantObjects(compliantObjects: CompliantObjectsProps) {
    this.compliantObjects = compliantObjects;
  }

  onChangeIngestionOption = (
    key: keyof IngestionOptionsSchema,
    value: boolean | string[] | null
  ) => {
    this.setSettings({
      ...this.settings,
      ingestion_options: {
        ...this.settings.ingestion_options,
        [key]: value,
      },
    });
  };

  onChangeValue = (value: FormValue, name?: string) => {
    if (name) {
      this.setSettings({
        ...this.settings,
        [name]: value,
      });
    }
    this.showMandatoryAlert && this.checkHasValidationsError();
  };

  checkCertificationsErrors(type: keyof BuildingCertificationSettings) {
    if (this.settings[type]?.is_active) {
      if (type === "dgnb23" || type === "qng") {
        if (!this.settings[type]?.building_type) return true;

        const selectedBuildingType = BUILDING_TYPES.find(
          (item) =>
            item.name === this.settings[type]?.building_type ||
            item.name_de === this.settings[type]?.building_type
        );
        const isResidentioalBuilding =
          selectedBuildingType?.id === BUILDING_TYPES[0].id;

        if (isResidentioalBuilding) {
          return !this.settings[type]?.energetic_standard;
        } else {
          return (
            !this.settings[type]?.level_of_requirements ||
            !this.settings[type]?.lca_class
          );
        }
      } else if (type === "bnb") {
        return !this.settings[type]?.net_floor_area;
      } else if (type === "dgnb18") {
        return !this.settings[type]?.relevant_area;
      }
    }
    return false;
  }

  checkHasValidationsError() {
    this.informationErrors =
      !this.settings.name ||
      !this.settings.net_floor_area ||
      !this.settings.gross_floor_area;

    this.certificationErrors =
      this.checkCertificationsErrors("dgnb23") ||
      this.checkCertificationsErrors("qng") ||
      this.checkCertificationsErrors("bnb") ||
      this.checkCertificationsErrors("dgnb18");

    this.setshowMandatoryAlert(
      this.informationErrors || this.certificationErrors
    );
  }

  async deleteImages() {
    const buildingID = buildingStore.buildingID;
    if (!buildingID) return;
    const authHeader = getLocalAuthHeader();
    await buildingsApi
      .concularApiV1RoutersBuildingsDeleteImgBuilding(
        buildingID,
        { images: this.imagesToRemove },
        authHeader
      )
      .then(async (response) => {
        await sidebarMenuStore.getCurrentOrgBuildings();
        this.setSettings({
          ...this.setSettings,
          images: response.data.images,
        });
        this.imagesToRemove = [];
      })
      .catch((err) => console.error(err, "BuildingsDeleteImgBuilding"));
  }

  async locationPlaceDetails(suggestion: SuggestOUT) {
    const authHeader = getLocalAuthHeader();
    return await locationsApi
      .concularApiV1RoutersLocationsPlaceDetails(
        suggestion.place_id,
        authHeader
      )
      .then((response) => {
        const place_details = response.data;
        this.onChangeValue(
          placeDetailsToString(place_details),
          "string_address"
        );
        return place_details;
      });
  }

  async createBuilding(data: BuildingIN) {
    const authHeader = getLocalAuthHeader();
    return await buildingsApi
      .concularApiV1RoutersBuildingsCreateOrgBuilding(data, authHeader)
      .then(async (response) => {
        this.setSettings({});
        await organisationStore.getAllOrganisations();
        await buildingStore.getOrganisationBuildings();
        await sidebarMenuStore.getCurrentOrgBuildings();
        buildingStore.setCurrentBuilding(response.data);
        return response;
      });
  }

  async updateBuildingImages() {
    const authHeader = getLocalAuthHeader();
    const buildingID = buildingStore.buildingID;
    if (this.settings.images?.length && buildingID) {
      return await buildingsApi.concularApiV1RoutersBuildingsUpdateImgBuilding(
        buildingID,
        { images: this.settings.images.map((item) => item.id) },
        authHeader
      );
    }
  }

  getBaseAmountMatrixKey(certKey: keyof BuildingCertificationSettings) {
    let base_amounts_matrix_key;
    const isResidentialBuilding =
      BUILDING_TYPES[0].name === this.settings[certKey]?.building_type ||
      BUILDING_TYPES[0].name_de === this.settings[certKey]?.building_type;

    if (isResidentialBuilding) {
      base_amounts_matrix_key = this.settings[
        certKey
      ]?.energetic_standard?.includes("> 40")
        ? "residential_over_40"
        : "residential_under_40";
      return base_amounts_matrix_key;
    }

    const buildingClass = this.settings[certKey]?.lca_class;
    if (this.settings[certKey]?.level_of_requirements?.includes("PLUS")) {
      if (
        buildingClass?.includes("K1") ||
        buildingClass?.includes("K4") ||
        buildingClass?.includes("K5")
      ) {
        base_amounts_matrix_key = "non_residential_k1_k4_k5_plus";
      } else if (
        buildingClass?.includes("K2") ||
        buildingClass?.includes("K3")
      ) {
        base_amounts_matrix_key = "non_residential_k2_k3_plus";
      } else {
        base_amounts_matrix_key = "non_residential_k6_plus";
      }
    } else if (
      buildingClass?.includes("K1") ||
      buildingClass?.includes("K4") ||
      buildingClass?.includes("K5")
    ) {
      base_amounts_matrix_key = "non_residential_k1_k4_k5_premium";
    } else if (buildingClass?.includes("K2") || buildingClass?.includes("K3")) {
      base_amounts_matrix_key = "non_residential_k2_k3_premium";
    } else {
      base_amounts_matrix_key = "non_residential_k6_premium";
    }
    return base_amounts_matrix_key;
  }

  buildingUpdateProps(data: BuildingIN & BuildingUpdateIN) {
    if (this.settings.construction_year)
      data["construction_year"] = this.settings.construction_year;
    if (this.settings.address) {
      const addressValues = this.settings.address as PlaceDetailsOUT;
      data["address"] = {
        country_code: addressValues.country_code,
        house_number: addressValues.house_number,
        latitude: addressValues.latitude,
        longitude: addressValues.longitude,
        postal_code: addressValues.postal_code,
        region: addressValues.region,
        street: addressValues.street,
      };
    }
    if (this.settings.building_type_id)
      data["building_type_id"] = this.settings.building_type_id;
    if (this.settings.gross_floor_area)
      data["gross_floor_area"] = Number(this.settings.gross_floor_area);
    if (this.settings.net_floor_area)
      data["net_floor_area"] = Number(this.settings.net_floor_area);
    if (this.settings.heated_area)
      data["heated_area"] = Number(this.settings.heated_area);
    data["upcoming_operation"] = this.settings.upcoming_operation;
    if (this.settings.start_date)
      data["start_date"] = moment(this.settings.start_date).format(
        "yyyy-MM-DD"
      );
    if (this.settings.end_date)
      data["end_date"] = moment(this.settings.end_date).format("yyyy-MM-DD");
    data["auto_premap"] = this.settings.auto_premap;
    data["premapping_enabled"] = this.settings.premapping_enabled;
    data["rule_based_mapping_enabled"] =
      this.settings.rule_based_mapping_enabled;
    data["ifc_parser_split_objects_list"] =
      this.settings.ifc_parser_split_objects_list;
    data["is_existing"] = this.settings.is_existing;
    data["calculation_option"] = {
      use_imported_mass: this.settings.use_imported_mass,
    };
    data["calculation_mode"] = this.settings.calculation_mode;
    if (this.settings.images?.length)
      data["images"] = this.settings.images.map((item) => item.id);
    const certSettings: BuildingCertificationSettings = {
      dgnb18: {
        ...this.settings.dgnb18,
        relevant_area: Number(this.settings.dgnb18?.relevant_area) || null,
      },
      dgnb23: {
        ...this.settings.dgnb23,
        base_amounts_matrix_key: this.getBaseAmountMatrixKey("dgnb23"),
      },
      qng: {
        ...this.settings.qng,
        base_amounts_matrix_key: this.getBaseAmountMatrixKey("qng"),
      },
      bnb: this.settings.bnb,
      levels: this.settings.levels,
    };
    // Tier 1 auto-clean: never persist is_active for certs the org license no
    // longer entitles (e.g. after a downgrade), incl. orgs with no license at all.
    // Only sanitize when entitlement is KNOWN (organisation + certification
    // catalogue loaded); otherwise an empty result would wrongly wipe every
    // is_active flag. Key off the organisation, not its license: an org with no
    // license is a valid zero-entitlement state, not an unloaded one.
    const organisation = sidebarMenuStore.currentOrganisation;
    const entitlementLoaded =
      Boolean(organisation) && conformityStore.certificationOptions.length > 0;
    if (entitlementLoaded) {
      const availableCertKeys = certificationIdsToKeys(
        organisation?.license?.selected_certification_ids,
        conformityStore.certificationOptions
      );
      (
        Object.keys(certSettings) as (keyof BuildingCertificationSettings)[]
      ).forEach((key) => {
        const option = certSettings[key];
        if (option && !availableCertKeys.includes(key)) {
          certSettings[key] = { ...option, is_active: false };
        }
      });
    }
    data["certification_export_settings"] = certSettings;
    if (this.settings.ifc_parser) data["ifc_parser"] = this.settings.ifc_parser;
    data["ingestion_options"] = this.settings.ingestion_options;
    data["life_span"] = this.settings.life_span || null;
    return data;
  }

  prefillDGNBFields() {
    const dgnbStore = DGNBStore.getInstance();
    dgnbStore.prefilDGNBFromBuildingFields(
      this.settings,
      buildingStore.buildingTypes
    );
  }

  async updateBuilding() {
    if (!buildingStore.buildingID) return;
    const data = this.buildingUpdateProps({
      name: this.settings.name,
    } as BuildingIN);
    await this.fetchUpdateBuilding(data);
  }

  async fetchUpdateBuilding(
    data: BuildingUpdateIN,
    options?: { silent?: boolean }
  ) {
    const authHeader = getLocalAuthHeader();
    if (!buildingStore.buildingID) return;
    return await buildingsApi
      .concularApiV1RoutersBuildingsUpdateOrgBuilding(
        buildingStore.buildingID,
        data,
        authHeader
      )
      .then(async (response) => {
        // `silent` skips the "building updated" flag so system-initiated writes
        // (e.g. the tier-3 certification reset) don't fire a success toast.
        if (!options?.silent) this.setBuildingAlreadyUpdated(true);
        await buildingStore.getOrganisationBuildings();
        await sidebarMenuStore.getCurrentOrgBuildings();
        buildingStore.setCurrentBuilding(response.data);
        this.prefillDGNBFields();
        this.setCompliantObjects({});
        await this.DatasetCompliance(data);
        await buildingStore.getCurrentBuildingDetails();
        await buildingStore.getBuildingCalculationsStatus();
        this.setSettings({});
      });
  }

  async uploadBuildingImages() {
    if (!this.settings.selectedImages?.length) return;
    const authHeader = getLocalAuthHeader();
    return await buildingsApi
      .concularApiV1RoutersBuildingsUploadBuildingImages(
        this.settings.selectedImages,
        authHeader
      )
      .then((response: AxiosResponse) => {
        this.setSettings({
          ...this.settings,
          images: response.data,
        });
      })
      .catch((err) => console.error(err, "BuildingsUploadBuildingImages"));
  }

  async fetchLocationSuggestions(value: string) {
    const authHeader = getLocalAuthHeader();
    return await locationsApi
      .concularApiV1RoutersLocationsSuggestPlaces(value, 5, 0, authHeader)
      .then((response) => response.data.items)
      .catch((err) => console.error(err, "LocationsSuggestPlaces"));
  }

  async deleteBuilding() {
    const buildingID = buildingStore.currentBuilding?.id;
    const authHeader = getLocalAuthHeader();
    if (!buildingID) return;
    await buildingsApi
      .concularApiV1RoutersBuildingsDeleteBuilding(buildingID, authHeader)
      .catch((err) => console.error(err, "BuildingsDeleteBuilding"));
  }

  async archiveBuilding() {
    const buildingID = buildingStore.currentBuilding?.id;
    const authHeader = getLocalAuthHeader();
    if (!buildingID) return;
    return await licensesApi
      .licenseApiV1RoutersLicensesArchiveBuilding(buildingID, authHeader)
      .catch((err) => {
        console.error(err, "LicensesArchiveBuilding");
        // Rethrow so the caller can surface the failure instead of showing a
        // false success toast / navigating away.
        throw err;
      });
  }

  getActiveCertifications() {
    const activeKeys = Object.entries(
      buildingStore.currentBuilding?.certification_export_settings ?? {}
    )
      .filter(Boolean)
      .filter((item) => item[1]?.is_active)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map((item) => item[0]);

    // Tier 1: drop certs the org license no longer entitles, so every consumer
    // (header switcher, operational emissions, building menu) stays consistent.
    // When entitlement isn't known yet, leave the list untouched (no over-hiding).
    // Key off the organisation, not its license: an org with no license is a valid
    // zero-entitlement state (filter everything), not an unloaded one.
    const organisation = sidebarMenuStore.currentOrganisation;
    const entitlementLoaded =
      Boolean(organisation) && conformityStore.certificationOptions.length > 0;
    if (!entitlementLoaded) return activeKeys;

    const availableKeys = certificationIdsToKeys(
      organisation?.license?.selected_certification_ids,
      conformityStore.certificationOptions
    );
    return activeKeys.filter((key) => availableKeys.includes(key));
  }

  hasAnyActiveCertifications() {
    return this.getActiveCertifications().length > 0;
  }

  reset() {
    this.imagesToRemove = [];
    this.settings = {};
    this.showMandatoryAlert = false;
    this.informationErrors = false;
    this.certificationErrors = false;
    this.openCertificationsTab = false;
  }

  static instance: BuildingSettingsStore;
  constructor() {
    makeAutoObservable(this);
  }

  private async DatasetCompliance(data: BuildingUpdateIN) {
    await buildingStore
      .fetchCheckDatasetCompliance(
        buildingStore.buildingID,
        data.certification_id,
        data.conformity_id
      )
      .then((response) => {
        this.setCompliantObjects({
          compliant: response?.compliant_objects,
          total: response?.total_objects,
        });
      })
      .catch((err) => console.error(err, "fetchCheckDatasetCompliance"));
  }

  static getInstance(): BuildingSettingsStore {
    if (!BuildingSettingsStore.instance) {
      BuildingSettingsStore.instance = new BuildingSettingsStore();
    }
    return BuildingSettingsStore.instance;
  }
}

export const buildingSettingsStore = BuildingSettingsStore.getInstance();
